export type AdminTab =
  | 'dashboard'
  | 'players'
  | 'scores'
  | 'partners'
  | 'calendar'
  | 'finance'
  | 'contents'
  | 'tournaments'
  | 'courses'
  | 'press'
  | 'analytics'
  | 'messaging'
  | 'interest'
  | 'settings'
  | 'account';

const ALL_TABS: AdminTab[] = [
  'dashboard', 'players', 'scores', 'partners', 'calendar',
  'finance', 'contents', 'tournaments', 'courses', 'press',
  'analytics', 'messaging', 'interest', 'settings', 'account',
];

const BUILTIN_ROLE_TABS: Record<string, AdminTab[]> = {
  admin: ALL_TABS,
  super_admin: ALL_TABS,
  user: ['dashboard', 'account'],
  manager: ['dashboard', 'players', 'scores', 'partners', 'calendar', 'finance', 'tournaments', 'press', 'analytics', 'messaging', 'account'],
  editor: ['dashboard', 'players', 'partners', 'calendar', 'contents', 'press', 'tournaments', 'courses', 'messaging', 'account'],
  viewer: ['dashboard', 'account'],
  scorer: ['dashboard', 'scores', 'messaging', 'account'],
  technical: ['dashboard', 'calendar', 'scores', 'players', 'messaging', 'account'],
};

const PERMISSION_TO_TABS: Record<string, AdminTab[]> = {
  dashboard:   ['dashboard'],
  players:     ['players'],
  scores:      ['scores'],
  partners:    ['partners'],
  events:      ['calendar'],
  financials:  ['finance'],
  content:     ['contents'],
  tournaments: ['tournaments'],
  courses:     ['courses'],
  press:       ['press'],
  analytics:   ['analytics'],
  messaging:   ['messaging'],
  settings:    ['settings', 'interest'],
  users:       ['settings'],
  account:     ['account'],
};

export function getAllowedTabs(
  role: string,
  customRolePermissions?: Record<string, any>
): AdminTab[] {
  const normalized = role?.toLowerCase() ?? '';

  if (BUILTIN_ROLE_TABS[normalized]) {
    return BUILTIN_ROLE_TABS[normalized];
  }

  if (!customRolePermissions) return [];

  const tabs = new Set<AdminTab>();

  for (const [permKey, grantedTabs] of Object.entries(PERMISSION_TO_TABS)) {
    const perm = customRolePermissions[permKey];
    const hasAccess =
      perm === true ||
      (typeof perm === 'object' && perm !== null && Object.values(perm).some(Boolean));
    if (hasAccess) {
      grantedTabs.forEach(t => tabs.add(t));
    }
  }

  return Array.from(tabs);
}

export function canAccessTab(
  tab: AdminTab,
  role: string,
  customRolePermissions?: Record<string, any>
): boolean {
  return getAllowedTabs(role, customRolePermissions).includes(tab);
}

export const TAB_TO_ROUTE: Record<AdminTab, string> = {
  dashboard:   'admin',
  players:     'admin/players',
  scores:      'admin/scores',
  partners:    'admin/partners',
  calendar:    'admin/events',
  finance:     'admin/finance',
  contents:    'admin/content',
  tournaments: 'admin/tournaments',
  courses:     'admin/courses',
  press:       'admin/press',
  analytics:   'admin/analytics',
  messaging:   'admin/messaging',
  interest:    'admin/interest',
  settings:    'admin/settings',
  account:     'admin/settings',
};
